import React from 'react';
import { ISearchCandidate } from '../../../../../shared/utils/interfaces/candidatesListFace';
import { highlightText } from '../../../../../shared/utils/functions/highlightText';
import CandidatesActions from '../CandidatesActions';
import { WithTooltip } from '../../../../../shared/components/tooltip.tsx/withTootip';
import uploadDisabled from '../../../../../shared/assets/images/uploadDisabled.svg';
import ExclamationYellow from '../../../../../shared/assets/images/ExclamationYellow.svg';
import { useNavigate } from 'react-router-dom';
import CVScore from '../CVScore';
import { truncate } from '../../../../../shared/utils/functions/typography';


export default function SearchCndidateCard({ candidate, query, onClick }: { candidate: ISearchCandidate, query: string, onClick: React.MouseEventHandler<HTMLElement> }) {
    const navigate = useNavigate();
    const handleTooltipClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/similarApplication/${candidate?.id}`);
    };
    return (
        <div className="relative flex-wrap items-center overflow-hidden transition-shadow duration-300 bg-white p-7 hover:shadow-md md:flex-nowrap justify-stretch rounded-2xl">
            <div className="flex w-full rounded-md cursor-pointer">
                <div className='w-[95%] md:w-3/4' onClick={onClick} >
                    <div className="flex flex-col items-start text-base font-bold text-gray-600 md:flex-row">
                        {candidate?.firstName && candidate?.lastName ?
                            <>
                                <CVScore cvScore={candidate?.cvScore} className='inline min-w-fit md:hidden' />
                                <span className='hidden capitalize md:inline'>{highlightText(`${candidate?.firstName} ${candidate?.lastName}`, query)}</span>
                                {candidate?.similarityTypes?.includes("name") && (
                                    <div className='items-center hidden md:flex'>
                                        <img src={ExclamationYellow} alt='Similarity detection' className='inline ms-0 md:ms-3' />
                                        <div className='self-center text-xs font-normal text-yellow-600'>Similar name</div>
                                    </div>
                                )}
                            </>
                            : <span className='hidden capitalize md:inline'>{candidate?.cvFileName}</span>}
                        <span>{candidate?.batchId &&
                            <WithTooltip
                                className='hidden w-36 md:block'
                                placement='right'
                                title='This candidate was uploaded by admin'
                            >
                                <img src={uploadDisabled} alt='This candidate was uploaded by admin' className='hidden md:inline ms-3' />
                            </WithTooltip>
                        }</span>
                        <CVScore cvScore={candidate?.cvScore} className='hidden ml-4 min-w-fit md:inline' />
                        {candidate?.isSimilar &&
                            <WithTooltip
                                className='hidden w-36 md:block'
                                placement='right'
                                colorText='#B45309'
                                backgroundColor='#FEF3C7'
                                width='w-48'

                                title={<>Similarity detection: It appears that the same candidate has applied for multiple positions.
                                    <h4 className='mt-3 cursor-pointer' onClick={handleTooltipClick}>Click to compare.</h4></>}
                            >
                                <img onClick={handleTooltipClick} src={ExclamationYellow} alt='Similarity detection' className='hidden md:inline ms-3' />
                            </WithTooltip>}
                    </div>
                    <div className={`flex md:hidden flex-nowrap ${candidate?.similarityTypes?.includes("name") ? 'flex-col' : 'flex-row'} text-base font-bold text-gray-600 w-fit`}>
                        {candidate?.firstName && candidate?.lastName ?
                            <>
                                <span className='inline-block capitalize whitespace-nowrap'>{highlightText(truncate(`${candidate?.firstName} ${candidate?.lastName}`,15), query)}</span>
                                {candidate?.similarityTypes?.includes("name") && (
                                    <div className='flex items-center'>
                                        <img src={ExclamationYellow} alt='Similarity detection' className='inline' />
                                        <div className='self-center text-xs font-normal text-yellow-600'>Similar name</div>
                                    </div>
                                )}
                            </>
                            : <span className='overflow-hidden capitalize whitespace-nowrap text-ellipsis'>{candidate?.cvFileName}</span>}
                        <span>{candidate?.batchId &&
                            <WithTooltip
                                className='w-36'
                                placement='right'
                                title='This candidate was uploaded by admin'
                            >
                                <img src={uploadDisabled} alt='This candidate was uploaded by admin' className='inline ms-3' />
                            </WithTooltip>
                        }</span>
                        {candidate?.isSimilar &&
                            <WithTooltip
                                className='w-36'
                                placement='right'
                                colorText='#B45309'
                                backgroundColor='#FEF3C7'
                                width='w-48'

                                title={<>Similarity detection: It appears that the same candidate has applied for multiple positions.
                                    <h4 className='mt-3 cursor-pointer' onClick={handleTooltipClick}>Click to compare.</h4></>}
                            >
                                <img onClick={handleTooltipClick} src={ExclamationYellow} alt='Similarity detection' className='inline ms-3' />
                            </WithTooltip>}
                    </div>
                    {candidate?.department && (
                        <div className='flex capitalize'>
                            <p className="text-sm font-normal text-gray-600">{candidate?.department?.parentDepartment?.name}</p>
                            {(candidate?.department?.parentDepartment?.name && candidate?.department?.name) ? <p className="mx-1 text-sm font-normal text-gray-600">/</p> : null}
                            <p className="text-sm font-normal text-gray-600">{candidate?.department?.name}</p>

                        </div>
                    )}
                    <p className="text-sm font-normal text-gray-600 capitalize">{candidate && highlightText(candidate?.currentPosition, query)}</p>
                    <div className="flex text-sm font-normal text-gray-600">
                        <p className="text-sm font-normal text-gray-600 capitalize">
                            <span className='mr-1 lowercase'>in</span>
                            {`${candidate?.title} ${candidate?.title && candidate?.stageName ? '/' : ''} ${highlightText(candidate?.stageName, query)}`}
                        </p>
                    </div>

                    {(candidate?.email?.toLowerCase()?.includes(query?.toLowerCase() ?? '') || candidate?.similarityTypes?.includes("email")) && (
                        <p className={`flex ${candidate?.similarityTypes?.includes("email")?'flex-col':'flex-row'} text-sm font-normal text-gray-600 md:flex-row`}>
                            {candidate?.email && (
                                <>
                                    {highlightText(candidate?.email, query)}
                                    {candidate?.similarityTypes?.includes("email") && (
                                        <div className="flex items-center">
                                            <img src={ExclamationYellow} alt="Similarity detection" className="inline ms-0 md:ms-3" />
                                            <div className="self-center text-xs font-normal text-yellow-600">Similar email address</div>
                                        </div>
                                    )}
                                </>
                            )}
                        </p>
                    )}

                    {candidate?.similarityTypes?.includes("phone") && candidate?.phone && (
                        <p className="flex flex-col text-sm font-normal text-gray-600 md:flex-row">
                            {highlightText(candidate?.phone, query)}
                            <div className="flex items-center">
                                <img src={ExclamationYellow} alt="Similarity detection" className="inline ms-0 md:ms-3" />
                                <div className='self-center text-xs font-normal text-yellow-600'>Similar phone number</div>
                            </div>
                        </p>
                    )}
                    <p className="text-sm font-normal text-gray-600">{candidate?.snippet ? <>..{highlightText(candidate?.snippet, query)}..</> : ''}</p>
                </div>
                <div className='content-center w-1/4 text-right' >
                    <CandidatesActions withDropDown={true} candidate={candidate} />
                </div>
            </div>
        </div >
    );
}
